import { useCallback, useEffect, useLayoutEffect, useRef, useState, type RefObject } from 'react';

/**
 * Motion helpers. The console animates with CSS (tokens and utilities in styles/index.css);
 * the little JavaScript here exists only to let something finish leaving before React
 * unmounts it. Every helper resolves at once in jsdom, which has no Web Animations, so
 * tests see the immediate behaviour the console had before it moved.
 *
 * The hooks that need to know what the previous render showed store it in state and adjust
 * it during render (the pattern React documents for information from previous renders),
 * rather than in refs, which must not be read while rendering.
 */

/** A leaving element never waits longer than this, even if a transition never starts. */
const SETTLE_TIMEOUT_MS = 1000;

/**
 * Resolve once every transition and animation on `el` (and inside it, with `subtree`) has
 * finished. Infinite animations, such as a spinner, are ignored.
 */
export function afterAnimations(
  el: Element | null,
  options: { subtree?: boolean } = {},
): Promise<void> {
  if (!el || typeof el.getAnimations !== 'function') return Promise.resolve();
  const running = el
    .getAnimations({ subtree: options.subtree ?? false })
    .filter((animation) => animation.effect?.getTiming().iterations !== Infinity);
  if (running.length === 0) return Promise.resolve();
  const finished = Promise.allSettled(running.map((animation) => animation.finished)).then(
    () => undefined,
  );
  const timeout = new Promise<void>((resolve) => {
    window.setTimeout(resolve, SETTLE_TIMEOUT_MS);
  });
  return Promise.race([finished, timeout]);
}

/**
 * Keep an element in the tree while it animates out. `present` stays true after `open`
 * turns false until the transitions on the referenced element have ended.
 */
export function usePresence<T extends HTMLElement>(
  open: boolean,
): {
  present: boolean;
  ref: RefObject<T | null>;
} {
  const ref = useRef<T | null>(null);
  const [wasOpen, setWasOpen] = useState(open);
  const [exiting, setExiting] = useState(false);

  if (open !== wasOpen) {
    setWasOpen(open);
    if (!open) setExiting(true);
  }

  useLayoutEffect(() => {
    if (open || !exiting) return;
    let cancelled = false;
    void afterAnimations(ref.current).then(() => {
      if (!cancelled) setExiting(false);
    });
    return () => {
      cancelled = true;
    };
  }, [open, exiting]);

  return { present: open || exiting, ref };
}

/**
 * The last non-null value, kept for `ms` after the value becomes null. Lets a caller that
 * mounts a dialog only while a choice is made keep it mounted, closed, long enough for its
 * exit transition.
 */
export function useLingering<T>(value: T | null, ms: number): T | null {
  const [kept, setKept] = useState<T | null>(value);

  if (value !== null && value !== kept) setKept(value);

  useEffect(() => {
    if (value !== null) return;
    const id = window.setTimeout(() => setKept(null), ms);
    return () => window.clearTimeout(id);
  }, [value, ms]);

  return value ?? kept;
}

/**
 * How many times `value` has changed since the first render. Keying an element on it
 * replays the element's entrance animation on change, and never on first paint.
 */
export function useChangeCount(value: unknown): number {
  const [seen, setSeen] = useState(value);
  const [count, setCount] = useState(0);

  if (!Object.is(seen, value)) {
    setSeen(value);
    setCount(count + 1);
  }

  return count;
}

/** How long a dialog is kept mounted after it is dismissed: its exit transition plus a margin. */
export const DIALOG_LINGER_MS = 260;

export interface ListEntry<T> {
  key: string;
  item: T;
  /** The item is no longer in the list and is animating out. */
  leaving: boolean;
}

interface Departed<T> {
  item: T;
  /** Where the item sat in the list it left, so it can keep that place while leaving. */
  index: number;
}

interface Seen<T> {
  /** The `items` array these entries were derived from (compared by identity). */
  items: readonly T[];
  /** The keys in the order they were last shown, departed ones included. */
  order: string[];
  leaving: Map<string, Departed<T>>;
}

function merge<T>(
  items: readonly T[],
  keyOf: (item: T) => string,
  leaving: Map<string, Departed<T>>,
): ListEntry<T>[] {
  const entries: ListEntry<T>[] = items.map((item) => ({ key: keyOf(item), item, leaving: false }));
  const departed = [...leaving.entries()].sort((a, b) => a[1].index - b[1].index);
  for (const [key, { item, index }] of departed) {
    entries.splice(Math.min(index, entries.length), 0, { key, item, leaving: true });
  }
  return entries;
}

/**
 * A list whose departed items linger, marked `leaving`, in the position they held, until
 * the caller reports each one gone (`left`). Items are compared by `keyOf`. Everything is
 * derived while rendering, so a departure never shows an empty frame first.
 */
export function useLeavingList<T>(
  items: readonly T[],
  keyOf: (item: T) => string,
): { entries: ListEntry<T>[]; left: (key: string) => void } {
  const [seen, setSeen] = useState<Seen<T>>(() => ({
    items,
    order: items.map(keyOf),
    leaving: new Map(),
  }));

  if (items !== seen.items) {
    const currentKeys = new Set(items.map(keyOf));
    const lastItems = new Map(seen.items.map((item) => [keyOf(item), item]));
    const leaving = new Map(seen.leaving);
    seen.order.forEach((key, index) => {
      if (currentKeys.has(key) || leaving.has(key)) return;
      const item = lastItems.get(key);
      if (item !== undefined) leaving.set(key, { item, index });
    });
    for (const key of leaving.keys()) if (currentKeys.has(key)) leaving.delete(key);
    setSeen({ items, order: merge(items, keyOf, leaving).map((e) => e.key), leaving });
  }

  const left = useCallback((key: string) => {
    setSeen((s) => {
      if (!s.leaving.has(key)) return s;
      const leaving = new Map(s.leaving);
      leaving.delete(key);
      return { ...s, leaving, order: s.order.filter((k) => k !== key) };
    });
  }, []);

  return { entries: merge(items, keyOf, seen.leaving), left };
}
